// Put the person's isolation back after the export borrows it.
//
// The export captures one page per LEVEL by isolating that level's uids
// for a single frame, then restoring what was isolated before. The
// restore read `[...store.isolated]` - and an isolation can be a RULE
// (FragmentIsolation, parked by setIsolationRule for a row too big to
// list), which answers has() and size but has NO Symbol.iterator. So the
// spread threw TypeError before the try block, captureLayouts' own
// .catch(() => ({ views: [], sections: [] })) swallowed it, and the PDF
// came out with no plan and no level pages at all - silently, and only
// for the people who had isolated a big row first.
//
// Hence one pair of functions with a test, rather than a spread at a call
// site: "an isolation is a list OR a rule" has to be true in both
// directions, and a restore that cannot fail loudly must not be able to
// fail quietly either.

/**
 * @param {Object|null} store ViewerStateStore
 * @returns {{kind: 'none'|'list'|'rule', uids?: string[], rule?: Object}}
 */
export function snapshotIsolation(store) {
  const iso = store && store.isolated;
  if (!iso) return { kind: 'none' };
  if (iso instanceof Set) return { kind: 'list', uids: [...iso] };
  if (typeof iso[Symbol.iterator] === 'function') return { kind: 'list', uids: [...iso] };
  // A rule: keep the object. It is immutable as far as this borrow is
  // concerned, and it is the only thing that can reproduce what it stood
  // for - we cannot enumerate a rule even in principle.
  return { kind: 'rule', rule: iso };
}

/**
 * Put back exactly what snapshotIsolation saw, including "nothing".
 * @returns {boolean} true when the store was restored
 */
export function restoreIsolation(store, snap) {
  if (!store || !snap) return false;
  if (snap.kind === 'rule' && snap.rule && typeof store.setIsolationRule === 'function') {
    store.setIsolationRule(snap.rule);
    return true;
  }
  if (typeof store.setIsolation !== 'function') return false;
  if (snap.kind === 'list') { store.setIsolation(snap.uids || []); return true; }
  store.setIsolation(null);
  return true;
}
